import type { Company } from "./types";

const HINT_KEY = "session.hint";

/**
 * Render-only hint (ADR-0006 D6′) — persists only the non-secret display fields
 * `{ id, name, timezone }` to `sessionStorage`. Survives the in-tab reload iOS
 * triggers on memory pressure; cleared when the tab is closed.
 *
 * Security invariant: this is never an authorization input — it is overwritten or
 * cleared by the server response on every boot.
 */

export function readHint(): Company | null {
  try {
    const raw = sessionStorage.getItem(HINT_KEY);
    if (!raw) return null;
    const p: unknown = JSON.parse(raw);
    if (
      typeof p !== "object" ||
      p === null ||
      typeof (p as Record<string, unknown>).id !== "string" ||
      typeof (p as Record<string, unknown>).name !== "string" ||
      typeof (p as Record<string, unknown>).timezone !== "string"
    ) {
      return null;
    }
    const r = p as Record<string, string>;
    return { id: r.id, name: r.name, timezone: r.timezone };
  } catch {
    return null;
  }
}

export function writeHint(c: Company): void {
  try {
    sessionStorage.setItem(
      HINT_KEY,
      JSON.stringify({ id: c.id, name: c.name, timezone: c.timezone }),
    );
  } catch {
    // storage quota / private-mode block — swallow, optimistic-only
  }
}

export function clearHint(): void {
  try {
    sessionStorage.removeItem(HINT_KEY);
  } catch {
    // swallow
  }
}
